import React, { memo, useState } from 'react';
import { Handle, Position } from 'reactflow';

// Color palette for phase badges - works with ANY phase name
// Uses a diverse set of distinguishable colors that look good as badges
const PHASE_COLOR_PALETTE = [
  { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },  // Blue
  { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },  // Green
  { bg: '#fef3c7', border: '#f59e0b', text: '#b45309' },  // Amber
  { bg: '#fce7f3', border: '#ec4899', text: '#be185d' },  // Pink
  { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca' },  // Indigo
  { bg: '#f3e8ff', border: '#a855f7', text: '#7e22ce' },  // Purple
  { bg: '#ccfbf1', border: '#14b8a6', text: '#0f766e' },  // Teal
  { bg: '#fef9c3', border: '#eab308', text: '#a16207' },  // Yellow
  { bg: '#ffe4e6', border: '#f43f5e', text: '#be123c' },  // Rose
  { bg: '#cffafe', border: '#06b6d4', text: '#0e7490' },  // Cyan
];

// Special colors for specific node types
const SPECIAL_COLORS = {
  objection: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c' },  // Red - always for objections
  default: { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' },    // Gray - fallback
};

// Simple hash function to get consistent color index from any string
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Get color for any phase name - consistent colors for same name
const getPhaseColor = (step, isObjection = false) => {
  // Objections always get red
  if (isObjection) return SPECIAL_COLORS.objection;
  
  // No step = default gray
  if (!step) return SPECIAL_COLORS.default;
  
  // Hash the step name to get a consistent color index
  const colorIndex = hashString(step.toLowerCase()) % PHASE_COLOR_PALETTE.length;
  return PHASE_COLOR_PALETTE[colorIndex];
};

const QuestionNode = memo(function QuestionNode({ data, selected }) {
  const [hover, setHover] = useState(false);

  const isObjection = (data?.id || data?.title || '').toString().startsWith('objection_');
  const phaseColor = getPhaseColor(data?.step, isObjection);
  
  const containerStyle = {
    border: selected ? '2px solid #1c64f2' : '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '20px',
    background: data?.isOpeningNode ? '#ecfdf5' : data?.isEndNode ? '#fef3c7' : isObjection ? '#fef2f2' : '#ffffff',
    width: '350px',
    boxShadow: selected 
      ? '0 8px 25px -8px rgba(28, 100, 242, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
      : hover 
        ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    position: 'relative',
    cursor: 'grab',
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    userSelect: 'none',
  };

  return (
    <div 
      style={containerStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {data.onDelete && !data.isEndNode && (
        <button
          onClick={data.onDelete}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: hover ? '#fef2f2' : 'transparent',
            border: hover ? '1px solid #fecaca' : 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: hover ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            color: '#ef4444',
            transition: 'all 0.2s ease',
          }}
          title="Delete Node"
          onMouseEnter={(e) => {
            e.target.style.background = '#fef2f2';
            e.target.style.color = '#dc2626';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fef2f2';
            e.target.style.color = '#ef4444';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
      )}

      <div style={{ pointerEvents: 'none' }}>
        {/* Header row with Phase Badge and Status Indicators */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          {/* Phase Badge */}
          {data?.step && !data?.isEndNode && (
            <div style={{ 
              fontSize: '10px', 
              color: phaseColor.text,
              fontWeight: '600',
              background: phaseColor.bg,
              padding: '3px 8px',
              borderRadius: '4px',
              border: `1px solid ${phaseColor.border}`,
              textTransform: 'capitalize',
              letterSpacing: '0.3px',
            }}>
              {data.stepName || data.step}
            </div>
          )}
          
          {/* Opening Node Indicator */}
          {data?.isOpeningNode && (
            <div style={{ 
              fontSize: '10px', 
              color: '#059669',
              fontWeight: '600',
              background: '#d1fae5',
              padding: '3px 8px',
              borderRadius: '4px',
              border: '1px solid #10b981',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <span style={{ fontSize: '12px' }}>▶</span> Start
            </div>
          )}
          
          {/* End Node Indicator */}
          {data?.isEndNode && (
            <div style={{ 
              fontSize: '10px', 
              color: '#b45309',
              fontWeight: '600',
              background: '#fef3c7',
              padding: '3px 8px',
              borderRadius: '4px',
              border: '1px solid #f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <span style={{ fontSize: '12px' }}>🏁</span> End
            </div>
          )}
        </div>
        
        {/* Question ID - Smaller and less prominent */}
        <div style={{ 
          fontSize: '11px', 
          color: '#9ca3af', 
          marginBottom: '12px', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px',
          fontWeight: '500',
          background: '#f9fafb',
          padding: '4px 8px',
          borderRadius: '6px',
          display: 'inline-block',
          border: '1px solid #f3f4f6'
        }}>
          {data?.title}
        </div>
                
        {/* Script Preview - Larger and more prominent */}
        <div style={{ 
          color: '#1f2937', 
          fontSize: '15px', 
          lineHeight: '1.6', 
          fontWeight: '500', 
          minHeight: '70px',
          background: '#f8fafc',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          {data?.snippet ? `${data.snippet}...` : 'No script content...'}
        </div>
      </div>

      {data.onEdit && (
        <button 
          onClick={data.onEdit}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            background: hover ? '#3b82f6' : '#f3f4f6',
            color: hover ? 'white' : '#374151',
            border: hover ? 'none' : '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            pointerEvents: 'auto',
            transition: 'all 0.2s ease',
            boxShadow: hover ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
          }}
        >
                    Edit
        </button>
      )}

      {/* Target Handle - TOP ONLY (inward arrow in circle) */}
      <Handle 
        type="target" 
        id="in-top" 
        position={Position.Top} 
        style={{ 
          background: 'transparent',
          border: 'none',
          width: '22px',
          height: '22px',
          top: '-11px',
          zIndex: 10,
        }} 
        isConnectable 
      />
      <div style={{ position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 9 }}>
        <svg width="20" height="20" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" fill="white" stroke="#9ca3af" strokeWidth="1.5" />
          <path d="M10 5 L10 14 M6 10 L10 14 L14 10" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Source Handles - LEFT, BOTTOM, RIGHT (outward arrows in circles) - Only for non-end nodes */}
      {!data?.isEndNode && (
        <>
          {/* Left source */}
          <Handle type="source" id="out-left" position={Position.Left} 
            style={{ background: 'transparent', border: 'none', width: '22px', height: '22px', left: '-11px', zIndex: 10 }} isConnectable />
          <div style={{ position: 'absolute', left: '-13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 9 }}>
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="white" stroke="#93c5fd" strokeWidth="1.5" />
              <path d="M14 10 L6 10 M10 6 L6 10 L10 14" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Bottom source */}
          <Handle type="source" id="out-bottom" position={Position.Bottom} 
            style={{ background: 'transparent', border: 'none', width: '22px', height: '22px', bottom: '-11px', zIndex: 10 }} isConnectable />
          <div style={{ position: 'absolute', bottom: '-13px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 9 }}>
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="white" stroke="#93c5fd" strokeWidth="1.5" />
              <path d="M10 5 L10 14 M6 10 L10 14 L14 10" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Right source */}
          <Handle type="source" id="out-right" position={Position.Right} 
            style={{ background: 'transparent', border: 'none', width: '22px', height: '22px', right: '-11px', zIndex: 10 }} isConnectable />
          <div style={{ position: 'absolute', right: '-13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 9 }}>
            <svg width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="white" stroke="#93c5fd" strokeWidth="1.5" />
              <path d="M6 10 L14 10 M10 6 L14 10 L10 14" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </>
      )}
    </div>
  );
});

export default QuestionNode;



